import { MediaContentType, MediaFolder } from "../../interfaces/MediaObject";
import { postRequest, putToSignedUrl } from "../http/httpRequest";
import { extractApiErrorMessage, fetchOrThrowFriendly } from "../helpers/ApiErrorHelper";

/**
 * Result of requesting a presigned upload slot. `uploadUrl` is valid for ~10 min
 * and is where the raw bytes are PUT; `publicUrl` is the permanent fetch URL to
 * persist in the DB; `objectPath` is the storage path (used for persist/delete).
 */
export interface UploadTarget {
  objectPath: string;
  uploadUrl: string;
  publicUrl: string;
}

export interface RequestUploadParams {
  fileName: string;
  folder: MediaFolder;
  contentType: MediaContentType;
}

/**
 * Step 1 of the upload pipeline: asks the backend for a presigned upload slot.
 * Returns the storage path + the short-lived uploadUrl + the permanent publicUrl.
 */
async function requestUpload(params: RequestUploadParams): Promise<UploadTarget> {
  const endpoint = "/api/v1/storage/upload";
  const response = await fetchOrThrowFriendly(() => postRequest(endpoint, params, true));

  if (!response.ok) {
    const data = await response.json().catch(() => null);
    throw new Error(extractApiErrorMessage(data) || "Couldn't start the upload. Please try again.");
  }

  const data = await response.json().catch(() => null);
  const target = data?.contents?.[0];
  if (!target?.uploadUrl || !target?.publicUrl || !target?.path) {
    throw new Error(extractApiErrorMessage(data) || "Couldn't start the upload. Please try again.");
  }

  return {
    objectPath: target.path as string,
    uploadUrl: target.uploadUrl as string,
    publicUrl: target.publicUrl as string,
  };
}

/**
 * Step 2 of the upload pipeline: PUTs the file bytes to the presigned URL.
 * Throws on a non-2xx so the caller can attribute the failure to the "put" stage.
 */
async function uploadToTarget(
  target: UploadTarget,
  file: Blob,
  contentType: MediaContentType
): Promise<void> {
  const response = await fetchOrThrowFriendly(() =>
    putToSignedUrl(target.uploadUrl, file, contentType)
  );
  if (!response.ok) {
    // Keep the status for debugging, but lead with copy a user understands.
    throw new Error(`Couldn't upload this file. Please try again. (error ${response.status})`);
  }
}

const StorageApiService = {
  requestUpload,
  uploadToTarget,
};

export default StorageApiService;
